import { useCallback, useMemo } from 'react';
import { useAuth as useOidcAuth } from 'react-oidc-context';

export interface AppUser {
  sub: string;
  username: string;
  email: string;
  name: string | null;
  roles: string[];
}

interface RealmAccessClaim {
  roles?: string[];
}

interface KeycloakProfile {
  sub?: string;
  preferred_username?: string;
  email?: string;
  name?: string;
  realm_access?: RealmAccessClaim;
}

export interface UseAuthResult {
  user: AppUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: Error | null;
  accessToken: string | undefined;
  login: () => void;
  logout: () => void;
  /**
   * UI gating only — never use this to enforce authorisation. The backend
   * validates every request against the JWT independently; this client-side
   * check is purely cosmetic (e.g. hiding nav links a user cannot reach) and
   * is trivially bypassable by a malicious client.
   */
  hasRole: (role: string) => boolean;
}

// Decode a JWT body without verifying the signature. Verification is the
// backend's job; here we only need the claims for UI gating. Returns null if
// the token shape is malformed.
function decodeJwtPayload(token: string | undefined): { realm_access?: RealmAccessClaim } | null {
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    // base64url → base64. atob handles standard base64 only.
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    return JSON.parse(atob(padded)) as { realm_access?: RealmAccessClaim };
  } catch {
    return null;
  }
}

export function useAuth(): UseAuthResult {
  const oidc = useOidcAuth();
  const profile = oidc.user?.profile as KeycloakProfile | undefined;
  const accessToken = oidc.user?.access_token;

  const sub = profile?.sub;
  const username = profile?.preferred_username;
  const email = profile?.email;
  const name = profile?.name;

  // Keycloak's default configuration puts realm roles in the access token,
  // NOT the ID token (which is what `profile` reflects). Without this, every
  // hasRole('admin') call would return false in production even when the
  // user genuinely has the admin role. Union both sources so:
  //   - real Keycloak tokens (roles in access_token) work, and
  //   - tests that stub roles via profile keep working.
  const roles = useMemo<string[]>(() => {
    const fromProfile = profile?.realm_access?.roles ?? [];
    const accessClaims = decodeJwtPayload(accessToken);
    const fromAccess = accessClaims?.realm_access?.roles ?? [];
    return Array.from(new Set([...fromProfile, ...fromAccess]));
  }, [profile, accessToken]);

  const user = useMemo<AppUser | null>(() => {
    if (!sub || !username || !email) {
      return null;
    }
    return {
      sub,
      username,
      email,
      name: name ?? null,
      roles,
    };
  }, [sub, username, email, name, roles]);

  const login = useCallback(() => {
    void oidc.signinRedirect();
  }, [oidc]);

  const logout = useCallback(() => {
    void oidc.signoutRedirect();
  }, [oidc]);

  const hasRole = useCallback((role: string) => (user?.roles ?? []).includes(role), [user]);

  return {
    user,
    isAuthenticated: oidc.isAuthenticated,
    isLoading: oidc.isLoading,
    error: oidc.error ?? null,
    accessToken,
    login,
    logout,
    hasRole,
  };
}
